import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC, useCallback, useEffect, useState } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAccordionComponent from 'src/components/ui-component/mui-accordion/MUIAccordionComponent';
import { useApplication } from 'src/context/ApplicationContext';
import GetRecords from 'src/lib/api/GetRecords';
import { APIS } from 'src/lib/constants/apis';
import { ANTRAG_VERBUNDPARTNER, KMU_PATHS, RUS_PATH } from 'src/lib/constants/componentsPath';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, ANTRAGS_FORMULAREN, FORM_FIELDS_TYPES, NICHT_IN_HESSEN } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { EmailValidation, ForceTrue, GetMinMaxDecimalValidation, PhoneNumberValidation, Required } from 'src/lib/validation/ValidationTypes';
import { checkIfSpecificPartnerHasComment } from 'src/pages/private/online-antrag/application-helper-function/PruefAssistenzHelperFunktion';
import {
	addIndexesToPath,
	addIndexToPath,
	checkReadOnlyInUeberarbeitung,
	generateUniqueId,
	getFieldNamefromDataPath,
	getOptionsFromRes,
	isTrue,
	removeDataKeys,
	removeNullDeep,
} from 'src/utils/helper';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import {
	checkIfPruefAssitenzNeedsToValidate,
	getInstitutionsTypOption,
	getIsAntragsberechtigAlsHochschule,
} from '../../../../application-helper-function/AppHelperfunctions';
import { isPartnerSelectFieldDisabled, mapVerbundPartner, mapVerbundPartnerAnsprechpartner } from '../../../../application-helper-function/mapping';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import HochschuleSchema from 'src/lib/paths/hochschule_FULL_Paths.json';
import OrganisationSchema from 'src/lib/paths/organisation_FULL_Paths.json';
const P = AntragSchema.attributes;
const ORGA_P = OrganisationSchema.attributes;
const HS_P = HochschuleSchema.attributes;

const VerbundpartnerSkizzeGlobal: FC<{ hasAnyPartnerComment: boolean }> = ({ hasAnyPartnerComment }) => {
	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();

	const [hochschulen, setHochschulen] = useState<any[]>([]);

	const updatedReadOnlyHasAnyPartnerComment = checkReadOnlyInUeberarbeitung(application, readOnly, hasAnyPartnerComment);

	// const [verbundpartnerOptions, setVerbundpartnerOptions] = useState([]);

	// Check if the Delete Button for Verbundpartner can be shown.
	// Verbundpartner cannot be deleted if there is only one left or if for the partner any kostenposition is linked
	// (in this case the select field for partner will be disabled and the user gets informed via alert to first delete the kostenposition).
	const isVPartnerDeleteAllowed = useCallback(
		(index: number) => {
			const vPartner = get(application, P.Verbundpartner.path, []).length;
			const isPartnerSelectFieldDisabledFlag = isPartnerSelectFieldDisabled(application, index, updatedReadOnlyHasAnyPartnerComment);
			if (vPartner > 1 && !isPartnerSelectFieldDisabledFlag) {
				return true;
			}
			return false;
		},
		[application, updatedReadOnlyHasAnyPartnerComment],
	);

	useEffect(() => {
		const fetschHochschulen = async () => {
			try {
				const filter = {
					Im_Portal_In_Der_Auswahlliste_Anzeigen: true,
				};
				const res = await GetRecords(APIS.Hochschule, [], filter);

				if (res) {
					const resData = removeNullDeep(removeDataKeys(get(res, 'data', [])));
					setHochschulen(resData);
				}
			} catch (Err) {
				console.error('Error fetching Hochschulen: ', Err);
			}
		};

		fetschHochschulen();
	}, []);

	const updateVPartner = (key: string, value: any, partnerIndex: number) => {
		let appCopy = cloneDeep(application);
		set(appCopy, key, value);

		// Wenn eine echte Hochschule ausgewählt ist, deren Adressdaten in Verbundpartner-Felder übernehmen
		const isNeueHochschuleForPartner = !!get(
			appCopy,
			addIndexesToPath(P.Verbundpartner.schema.attributes.Neue_Hochschule.path, [`${partnerIndex}`], ['Verbundpartner']),
			false,
		);
		if (!isNeueHochschuleForPartner) {
			const hochschuleValue = get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Hochschule.path, [`${partnerIndex}`], ['Verbundpartner']),
				null,
			);
			const hochschuleId = typeof hochschuleValue === 'object' ? get(hochschuleValue, 'id', null) : hochschuleValue;
			const selectedHochschule = (hochschulen || []).find((ele: any) => get(ele, 'id') === Number(hochschuleId));

			if (selectedHochschule && hochschuleId) {
				// Adressdaten der Hochschule in Verbundpartner-Felder übernehmen
				set(
					appCopy,
					addIndexesToPath(
						P.Verbundpartner.schema.attributes.Verbundpartner_Strasse_Und_Hausnummer.path,
						[`${partnerIndex}`],
						['Verbundpartner'],
					),
					get(selectedHochschule, HS_P.Adressdaten_Strasse_Und_Hausnummer.path),
				);
				set(
					appCopy,
					addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_PLZ.path, [`${partnerIndex}`], ['Verbundpartner']),
					get(selectedHochschule, HS_P.Adressdaten_PLZ.path),
				);
				set(
					appCopy,
					addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Ort.path, [`${partnerIndex}`], ['Verbundpartner']),
					get(selectedHochschule, HS_P.Adressdaten_Ort.path),
				);
				set(
					appCopy,
					addIndexesToPath(P.Verbundpartner.schema.attributes.Kuerzel.path, [`${partnerIndex}`], ['Verbundpartner']),
					get(selectedHochschule, HS_P.Geschaeftsbezogene_Daten_Kuerzel.path),
				);

				// Hochschule-Objekt mit Adressdaten aufbauen
				let hochschuleData: any = { id: Number(hochschuleId) };
				const hochschulePaths = {
					[HS_P.Adressdaten_Strasse_Und_Hausnummer.path]: get(selectedHochschule, HS_P.Adressdaten_Strasse_Und_Hausnummer.path),
					[HS_P.Adressdaten_PLZ.path]: get(selectedHochschule, HS_P.Adressdaten_PLZ.path),
					[HS_P.Adressdaten_Ort.path]: get(selectedHochschule, HS_P.Adressdaten_Ort.path),
					[HS_P.Geschaeftsbezogene_Daten_Kuerzel.path]: get(selectedHochschule, HS_P.Geschaeftsbezogene_Daten_Kuerzel.path),
				};
				for (const [hsKey, hsValue] of Object.entries(hochschulePaths)) {
					set(hochschuleData, hsKey, hsValue);
				}
				set(
					appCopy,
					addIndexesToPath(P.Verbundpartner.schema.attributes.Hochschule.path, [`${partnerIndex}`], ['Verbundpartner']),
					hochschuleData,
				);
			}
		}

		// Für jeden Antrag gibt es eigenen Verbundpartner-Datensatz als Organisation.
		// Wenn die ID von Verbundpartner-Organisation vorhanden ist, dann soll die Organisation aktualisiert werden.
		// Wenn keine ID vorhanden ist, dann soll eine Verbundpartner-Organisation angelegt werden.
		const linkedPartnerId = get(
			application,
			`${addIndexesToPath(`${P.Verbundpartner.schema.attributes.Verbundpartner.path}.id`, [`${partnerIndex}`], ['Verbundpartner'])}`,
			'',
		);
		let orgaData = {
			...(Number(linkedPartnerId) > 0 ? { id: linkedPartnerId } : {}),
			attributes: {
				Antragstellerfuer_Verbundpartner: get(appCopy, `${P.Antragstellende_Organisation.path}.id`),
				Geschaeftsbezogene_Daten_Rolle: ['Projektpartner'],
			},
		};
		const paths = {
			[ORGA_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path]: get(
				appCopy,
				addIndexesToPath(
					P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path,
					[`${partnerIndex}`],
					['Verbundpartner'],
				),
			),
			[ORGA_P.Geschaeftsbezogene_Daten_Kuerzel.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Kuerzel.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
			[ORGA_P.Adressdaten_Strasse_Und_Hausnummer.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Strasse_Und_Hausnummer.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
			[ORGA_P.Adressdaten_PLZ.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_PLZ.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
			[ORGA_P.Adressdaten_Ort.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Ort.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
			[ORGA_P.Kontaktdaten_E_Mail.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_E_Mail_Adresse.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
			[ORGA_P.Kontaktdaten_Telefon.path]: get(
				appCopy,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Telefonnummer.path, [`${partnerIndex}`], ['Verbundpartner']),
			),
		};

		for (const [orgaKey, orgaValue] of Object.entries(paths)) {
			set(orgaData, orgaKey, orgaValue);
		}

		set(appCopy, addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner.path, [`${partnerIndex}`], ['Verbundpartner']), orgaData);

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	/**
	 * Laut das Ticket HA-2313 und HA-2314 muss die AW für alle angelegte Partner weg.
	 * Es kann sein, dass HA wieder die Liste haben will.
	 * Daher erstmal nur Auskommentieren.
	 * Es gibt auch andere Funktionen weiter unten, die auskommentiert sind.
	 * z.B. die Funktion in useEffect, formStructListVP und die Funktion weiter unten im getFormStrucVerbundpartnerAntragGlobal
	 * */
	/*
		const handleSelectVPartner = (key: string, value: any, partnerIndex: number) => {
		// autofold/fold_next
		let appCopy = cloneDeep(application);

		const selectedVPartner = verbundpartnerOptions.find((ele) => get(ele, 'id') === parseInt(value));
		let vPartnerData = {};
		const paths = {
			[addIndexesToPath(
				P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path,
				[`${partnerIndex}`],
				['Verbundpartner'],
			)]: ORGA_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path,
			[addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Strasse_Und_Hausnummer.path, [`${partnerIndex}`], ['Verbundpartner'])]:
				ORGA_P.Adressdaten_Strasse_Und_Hausnummer.path,
			[addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_PLZ.path, [`${partnerIndex}`], ['Verbundpartner'])]:
				ORGA_P.Adressdaten_PLZ.path,
			[addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Ort.path, [`${partnerIndex}`], ['Verbundpartner'])]:
				ORGA_P.Adressdaten_Ort.path,
			[addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_E_Mail_Adresse.path, [`${partnerIndex}`], ['Verbundpartner'])]:
				ORGA_P.Kontaktdaten_E_Mail.path,
			[addIndexesToPath(P.Verbundpartner.schema.attributes.Verbundpartner_Telefonnummer.path, [`${partnerIndex}`], ['Verbundpartner'])]:
				ORGA_P.Kontaktdaten_Telefon.path,
		};
		for (const [pathComponent, pathOrga] of Object.entries(paths)) {
			set(appCopy, pathComponent, get(selectedVPartner, pathOrga, null));
		}
		set(appCopy, P.Verbundpartner.path + partnerIndex, vPartnerData);
		set(appCopy, key, value);
		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};
	*/

	const handleAddPartner = () => {
		const positionID = generateUniqueId();

		try {
			let appCopy = cloneDeep(application);
			// anlegen von neuen Verbundpartner
			const key = `${P.Verbundpartner.path}.${get(application, P.Verbundpartner.path, []).length}.Verbundpartner_Strasse_Und_Hausnummer`;
			// eine unique-group-id für Verbundpartner vergeben
			const keyPositionId = `${P.Verbundpartner.path}.${get(application, P.Verbundpartner.path, []).length}.Position_ID`;
			const keyAnsprechpartner = `${P.Verbundpartner.path}.${get(application, P.Verbundpartner.path, []).length}.Verbundpartner_Ansprechpartnerin`;
			const keyKMU = `${P.Selbsterklaerung_KMU.path}.${get(application, P.Selbsterklaerung_KMU.path, []).length}.Kmu_Kennzahlen`;
			const keyKMUPositionId = `${P.Selbsterklaerung_KMU.path}.${get(application, P.Selbsterklaerung_KMU.path, []).length}.Position_ID`;
			const keyRUSPositionId = `${P.Selbsterklaerung_RUS.path}.${get(application, P.Selbsterklaerung_RUS.path, []).length}.Position_ID`;
			const keyProjektUebersichtPositionId = `${P.Projektbeschreibung_Loewe.schema.attributes.Projektuebersicht.path}.${get(application, P.Projektbeschreibung_Loewe.schema.attributes.Projektuebersicht.path, []).length}.Position_ID`;
			const valueKMU = [{ Kmu_Kennzahlen_Bezugsjahr: null }, { Kmu_Kennzahlen_Bezugsjahr: null }];
			const valueAnsprecherPartner = [{ Vorname: '' }];

			set(appCopy, key, '');
			set(appCopy, keyPositionId, positionID);
			set(appCopy, keyAnsprechpartner, valueAnsprecherPartner);
			set(appCopy, keyKMU, valueKMU);
			set(appCopy, keyKMUPositionId, positionID);
			set(appCopy, keyRUSPositionId, positionID);
			set(appCopy, keyProjektUebersichtPositionId, positionID);

			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Adding Verbundpartner', err);
		}
	};
	const handleDeletePartner = (index: number) => {
		try {
			let appCopy = cloneDeep(application);
			const allVparter = get(appCopy, P.Verbundpartner.path, []);

			// Finding the verbundpartner-Object with the index in order to get the position-ID from Partner
			const selectedVpartnerObj = allVparter.find((_: any, i: number) => i === index);
			const positionIDVpartner = get(selectedVpartnerObj, ANTRAG_VERBUNDPARTNER.PositionID);

			set(
				appCopy,
				P.Verbundpartner.path,
				allVparter.filter((_: any, i: number) => i !== index),
			);

			const allSelbstKmu = get(appCopy, P.Selbsterklaerung_KMU.path, []);
			// filtering out the KMU-Object where the position-ID is equal to the position-OD from selected-verbundpartner-object "positionIDVpartner"
			set(
				appCopy,
				P.Selbsterklaerung_KMU.path,
				allSelbstKmu.filter(
					(ele: any) =>
						get(ele, KMU_PATHS.PositionID) !== positionIDVpartner ||
						get(ele, `${KMU_PATHS.Unternehmen}.id`) !== get(selectedVpartnerObj, `${ANTRAG_VERBUNDPARTNER.Partner}.id`),
				),
			);

			const allSelbstRUS = get(appCopy, P.Selbsterklaerung_RUS.path, []);
			// filtering out the KMU-Object where the position-ID is equal to the position-OD from selected-verbundpartner-object "positionIDVpartner"
			set(
				appCopy,
				P.Selbsterklaerung_RUS.path,
				allSelbstRUS.filter(
					(ele: any) =>
						get(ele, RUS_PATH.PositionID) !== positionIDVpartner ||
						get(ele, `${RUS_PATH.Unternehmen}.id`) !== get(selectedVpartnerObj, `${ANTRAG_VERBUNDPARTNER.Partner}.id`),
				),
			);

			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Deleting Verbundpartner', err);
		}
	};

	const institutionsOptionsArr = get(application, P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte.path);

	const getSummaryTitle = (application: any, index: number) => {
		const partnerNumber = index + 1;
		const antragsformular = get(application, P.Allgemein_Antragsformular.path, '');
		const verbundparterName = get(
			application,
			addIndexesToPath(P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path, [`${index}`], ['Verbundpartner']),
			'',
		);
		const isPartnerHochschule = getIsAntragsberechtigAlsHochschule(application, index, institutionsOptionsArr);

		//Für Hochschule Kürzel benutzen, sonst Name
		if (isPartnerHochschule) {
			const verbundparterKuerzel = get(
				application,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Kuerzel.path, [`${index}`], ['Verbundpartner']),
				'',
			);
			return `Verbundpartner ${partnerNumber}${verbundparterKuerzel ? ` | ${verbundparterKuerzel}` : ''}`;
		}

		if (antragsformular === ANTRAGS_FORMULAREN.Loewe) {
			return `Verbundpartner ${partnerNumber}${verbundparterName ? ` | ${verbundparterName}` : ''}`;
		}

		return `Verbundpartner ${verbundparterName || `${partnerNumber} `}`;
	};

	return (
		<>
			{get(application, P.Verbundpartner.path, []).map((eachPartner: any, index: number) => {
				const hasComment = checkIfSpecificPartnerHasComment(readOnly, eachPartner);
				const antragBerechtigtSelected = get(
					application,
					addIndexToPath(P.Verbundpartner.schema.attributes.Antragsberechtigt_Als.path, index, P.Verbundpartner.path),
					null,
				);
				const isPartnerHochschule = getIsAntragsberechtigAlsHochschule(application, index, institutionsOptionsArr);
				const isNeueHochschule = !!get(
					application,
					addIndexesToPath(P.Verbundpartner.schema.attributes.Neue_Hochschule.path, [`${index}`], ['Verbundpartner']),
					false,
				);

				return (
					<MUIAccordionComponent
						key={index}
						summaryTitle={getSummaryTitle(application, index)}
						onDelete={() => handleDeletePartner(index)}
						forceShowDeleteButton={!isVPartnerDeleteAllowed(index)}
						deleteDisabledTitle={!isVPartnerDeleteAllowed(index) ? 'Löschen nicht möglich' : undefined}
						deleteDisabledReason={
							!isVPartnerDeleteAllowed(index) ?
								'In Ihrem Antrag sind einige Kosten mit diesem Verbundpartner verknüpft. Um den Verbundpartner aus dem Antrag zu entfernen, müssen Sie zunächst die entsprechenden Kosten löschen.'
							:	undefined
						}
						hasComment={hasComment}
					>
						<PruefAssistenz
							statusPath={addIndexesToPath(
								P.Verbundpartner.schema.attributes.Verbundpartner_Pruefung_Pruefstatus.path,
								[`${index}`],
								['Verbundpartner'],
							)}
							hinweisPath={addIndexesToPath(
								P.Verbundpartner.schema.attributes.Verbundpartner_Pruefung_Hinweis_Fuer_Den_Antragsteller.path,
								[`${index}`],
								['Verbundpartner'],
							)}
							ueberarbeitetAmPath={addIndexesToPath(
								P.Verbundpartner.schema.attributes.Verbundpartner_Pruefung_Ueberarbeitet_Am.path,
								[`${index}`],
								['Verbundpartner'],
							)}
							erledigtPath={addIndexesToPath(
								P.Verbundpartner.schema.attributes.Verbundpartner_Pruefung_Erledigt.path,
								[`${index}`],
								['Verbundpartner'],
							)}
							formStruc={mapVerbundPartner(formStrucPruefAssistenz(), index)}
						/>

						<CreateForm
							formStruc={mapVerbundPartner(formStrucTyp(application, index), index)}
							formValues={application}
							updateFormValuesFunc={(key, value) => updateVPartner(key, value, index)}
							readOnly={updatedReadOnlyHasAnyPartnerComment}
						/>
						{antragBerechtigtSelected && (
							<>
								{isPartnerHochschule && (
									<Stack spacing={2} sx={{ mb: '1rem', mt: '1rem' }}>
										{!isNeueHochschule ?
											<>
												<Alert severity={'info'}>
													Bitte prüfen Sie, ob Ihr Verbundpartner in der Auswahlliste "Hochschule" aufgeführt ist. Falls nicht - klicken
													Sie unten auf den Button "Neue Hochschule/Forschungseinrichtung anlegen".
													<Box sx={{ mt: 2 }}>
														<Button
															variant='outlined'
															onClick={() => {
																let appCopy = cloneDeep(application);
																const neueHochschulePath = addIndexesToPath(
																	P.Verbundpartner.schema.attributes.Neue_Hochschule.path,
																	[`${index}`],
																	['Verbundpartner'],
																);
																if (!isTrue(isNeueHochschule)) {
																	// In "Neue anlegen" wechseln: Neue_Hochschule=true, Hochschule-Relation leeren
																	set(appCopy, neueHochschulePath, true);
																	set(
																		appCopy,
																		addIndexesToPath(
																			P.Verbundpartner.schema.attributes.Hochschule.path,
																			[`${index}`],
																			['Verbundpartner'],
																		),
																		null,
																	);
																} else {
																	// Zurück zu "Aus Liste auswählen": Neue_Hochschule=false, Fachbereich leeren
																	set(appCopy, neueHochschulePath, false);
																	set(
																		appCopy,
																		addIndexesToPath(
																			P.Verbundpartner.schema.attributes.Fachbereich.path,
																			[`${index}`],
																			['Verbundpartner'],
																		),
																		'',
																	);
																	set(
																		appCopy,
																		addIndexesToPath(
																			P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path,
																			[`${index}`],
																			['Verbundpartner'],
																		),
																		'',
																	);
																}
																setApplicationProperty('attributes', get(appCopy, 'attributes'));
															}}
															sx={{ cursor: 'pointer', maxWidth: 'fit-content' }}
														>
															{isNeueHochschule ? 'Aus Liste auswählen' : 'Neue Hochschule/Forschungseinrichtung anlegen'}
														</Button>
													</Box>
												</Alert>
											</>
										:	<>
												<Button
													variant='outlined'
													onClick={() => {
														let appCopy = cloneDeep(application);
														const neueHochschulePath = addIndexesToPath(
															P.Verbundpartner.schema.attributes.Neue_Hochschule.path,
															[`${index}`],
															['Verbundpartner'],
														);
														if (!isTrue(isNeueHochschule)) {
															// In "Neue anlegen" wechseln: Neue_Hochschule=true, Hochschule-Relation leeren
															set(appCopy, neueHochschulePath, true);
															set(
																appCopy,
																addIndexesToPath(
																	P.Verbundpartner.schema.attributes.Hochschule.path,
																	[`${index}`],
																	['Verbundpartner'],
																),
																null,
															);
														} else {
															// Zurück zu "Aus Liste auswählen": Neue_Hochschule=false, Fachbereich leeren
															set(appCopy, neueHochschulePath, false);
															set(
																appCopy,
																addIndexesToPath(
																	P.Verbundpartner.schema.attributes.Fachbereich.path,
																	[`${index}`],
																	['Verbundpartner'],
																),
																'',
															);
															set(
																appCopy,
																addIndexesToPath(
																	P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path,
																	[`${index}`],
																	['Verbundpartner'],
																),
																'',
															);
														}
														setApplicationProperty('attributes', get(appCopy, 'attributes'));
													}}
													sx={{ cursor: 'pointer', maxWidth: 'fit-content' }}
												>
													{isNeueHochschule ? 'Aus Liste auswählen' : 'Neue Hochschule/Forschungseinrichtung anlegen'}
												</Button>
											</>
										}
									</Stack>
								)}
								<Grid2 spacing={6} container>
									<Grid2 sm={6}>
										<CreateForm
											formStruc={mapVerbundPartner(formStrucSpalte1(isPartnerHochschule, hochschulen, isNeueHochschule), index)}
											formValues={application}
											updateFormValuesFunc={(key, value) => updateVPartner(key, value, index)}
											readOnly={updatedReadOnlyHasAnyPartnerComment}
										/>
										<CreateForm
											formStruc={mapVerbundPartner(formStrucHochschuleAbweichendeSitz(application, isPartnerHochschule, index), index)}
											formValues={application}
											updateFormValuesFunc={(key, value) => updateVPartner(key, value, index)}
											readOnly={updatedReadOnlyHasAnyPartnerComment}
										/>
									</Grid2>
									<Grid2 sm={6}>
										<CreateForm
											formStruc={mapVerbundPartner(formStrucSpalte2(isPartnerHochschule, isNeueHochschule), index)}
											formValues={application}
											updateFormValuesFunc={(key, value) => updateVPartner(key, value, index)}
											readOnly={updatedReadOnlyHasAnyPartnerComment}
										/>
									</Grid2>
								</Grid2>
								<Box sx={{ mt: '1rem' }}>
									{isPartnerHochschule ?
										<Typography>
											Bitte geben Sie hier - neben den Mitarbeitenden im Projekt - auch die Professorin bzw. den Professor an, der für
											dieses Projekt verantwortlich ist.
										</Typography>
									:	<Typography>Bitte geben Sie hier die Geschäftsführung und die mitarbeitenden Personen im Projekt an.</Typography>}
								</Box>
								<CreateForm
									formStruc={mapVerbundPartner(formStrucVPAnsprechpartner(application), index)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={updatedReadOnlyHasAnyPartnerComment}
								/>
							</>
						)}
					</MUIAccordionComponent>
				);
			})}
			<Button sx={{ mt: '1rem' }} endIcon={<AddIcon />} onClick={handleAddPartner} disabled={updatedReadOnlyHasAnyPartnerComment}>
				Verbundpartner hinzufügen
			</Button>
		</>
	);
};

export default VerbundpartnerSkizzeGlobal;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Verbundpartner.schema.attributes.Verbundpartner_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};

const formStrucTyp = (application: any, _index: number) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Verbundpartner.schema.attributes.Antragsberechtigt_Als,
					sortAlphabetically: false,
					type: FORM_FIELDS_TYPES.singleSelect,
					options: getInstitutionsTypOption(application),
					label: 'Verbundpartner ist',
				},
			],
		},
	];
	return struc;
};

const formStrucSpalte1 = (isAntragsberechtigtHochschule: boolean, hochschulen?: any[], isNeueHochschule?: boolean) => {
	const hochSchulenOptions = getOptionsFromRes(
		hochschulen ? hochschulen : [],
		getFieldNamefromDataPath(HS_P.Geschaeftsbezogene_Daten_angezeigter_Name.path),
	);

	let struc: FormStrucItem[] = [
		...(isAntragsberechtigtHochschule ?
			([
				// Im "Neue anlegen"-Modus wird das Hochschule-Dropdown ausgeblendet
				...(!isNeueHochschule ?
					[
						{
							fields: [
								{
									...P.Verbundpartner.schema.attributes.Hochschule,
									type: FORM_FIELDS_TYPES.singleSelect,
									sortAlphabetically: true,
									options: hochSchulenOptions,
									fieldGroupWidth: '70%',
								},
								{
									...P.Verbundpartner.schema.attributes.Kuerzel,
									label: 'Kürzel',
									tooltip: 'Bitte Organisationskürzel angeben - dies wird für eine übersichtliche Darstellung im Portal genutzt.',
									fieldGroupWidth: '30%',
									disabled: true,
								},
							],
						},
					]
				:	[
						{
							fields: [
								{ ...P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation, fieldGroupWidth: '70%' },
								{
									...P.Verbundpartner.schema.attributes.Kuerzel,
									label: 'Kürzel',
									tooltip: 'Bitte Organisationskürzel angeben - dies wird für eine übersichtliche Darstellung im Portal genutzt.',
									fieldGroupWidth: '30%',
								},
							],
						},
					]),
				{
					fields: [
						{
							...P.Verbundpartner.schema.attributes.Verbundpartner_Strasse_Und_Hausnummer,
							disabled: !isNeueHochschule,
						},
					],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
				{
					fields: [
						{
							...P.Verbundpartner.schema.attributes.Verbundpartner_PLZ,
							disabled: !isNeueHochschule,
						},
						{
							...P.Verbundpartner.schema.attributes.Verbundpartner_Ort,
							disabled: !isNeueHochschule,
						},
					],
				},
				// Im "Neue anlegen"-Modus wird das Hochschule-Dropdown ausgeblendet
				...(!isNeueHochschule ?
					[]
				:	[
						{
							fields: [P.Verbundpartner.schema.attributes.Landkreis_47_Kreisfreie_Stadt],
							userProps: { direction: 'column', columnToDivide: 1 },
						},
					]),
			] as FormStrucItem[])
		:	[
				{
					fields: [
						{
							...P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation,
							tooltip:
								'Bitte geben Sie den vollständigen rechtsverbindlichen Namen Ihrer Organisation inklusive Rechtsform an (z. B. GmbH, UG (haftungsbeschränkt), AG, e. V.), wie im Handels- oder Vereinsregister eingetragen.',
						},
						P.Verbundpartner.schema.attributes.Verbundpartner_Strasse_Und_Hausnummer,
					],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
				{
					fields: [P.Verbundpartner.schema.attributes.Verbundpartner_PLZ, P.Verbundpartner.schema.attributes.Verbundpartner_Ort],
				},
				{
					fields: [
						{
							...P.Verbundpartner.schema.attributes.Landkreis_47_Kreisfreie_Stadt,
							sortAlphabetically: true,
							enumsLastPlace: [NICHT_IN_HESSEN],
						},
					],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
			]),
	];

	return struc;
};
const formStrucHochschuleAbweichendeSitz = (application: any, isPartnerHochschule: boolean, index: number) => {
	const abweichendeSitzPath = addIndexesToPath(P.Verbundpartner.schema.attributes.Abweichender_Sitz.path, [`${index}`], ['Verbundpartner']);

	let struc: FormStrucItem[] = [];

	if (isPartnerHochschule) {
		struc = [
			{
				fields: [{ ...P.Verbundpartner.schema.attributes.Abweichender_Sitz, label: 'Adresse Fachbereich, falls abweichend' }],
				userProps: { direction: 'column', columnToDivide: 1 },
			},
		];

		if (isTrue(get(application, abweichendeSitzPath))) {
			struc.push(
				{
					fields: [P.Verbundpartner.schema.attributes.Strasse_Und_Hausnummer],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
				{
					fields: [P.Verbundpartner.schema.attributes.PLZ, P.Verbundpartner.schema.attributes.Ort],
				},
			);
		}
	}

	return struc;
};

const formStrucSpalte2 = (isAntragsberechtigtAlsHochschule: boolean, isNeueHochschule?: boolean) => {
	const toolTipLoi = 'Eine Vorlage zur Erstellung des Letter of Intent ist bei den allgemeinen Downloads hinterlegt.';
	let struc: FormStrucItem[] = [
		...(isAntragsberechtigtAlsHochschule ?
			[
				{
					fields: [
						...(isNeueHochschule ?
							[P.Verbundpartner.schema.attributes.Fachbereich]
						:	[{ ...P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation, label: 'Fachbereich' }]),
						{ ...P.Verbundpartner.schema.attributes.Relevante_Kernkompentenz },
						{
							...P.Verbundpartner.schema.attributes.Letter_Of_Intent,
							tooltip: toolTipLoi,
						},
					],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
			]
		:	[
				{
					fields: [P.Verbundpartner.schema.attributes.Handelsregisternummer, P.Verbundpartner.schema.attributes.Gruendungsdatum],
				},
				{
					fields: [
						{ ...P.Verbundpartner.schema.attributes.Anzahl_Der_Mitarbeiter_40_JAE_41, label: 'Anzahl Mitarbeitende' },
						P.Verbundpartner.schema.attributes.Jahresumsatz,
					],
				},
				{
					fields: [
						P.Verbundpartner.schema.attributes.Branche,
						{
							...P.Verbundpartner.schema.attributes.F_E_Anteil,
							label: 'F&E-Anteil (% vom Umsatz)',
							validation: [{ validateFunction: GetMinMaxDecimalValidation(0, 100) }],
						},
					],
				},
				{
					fields: [
						P.Verbundpartner.schema.attributes.Relevante_Kernkompentenz,
						{
							...P.Verbundpartner.schema.attributes.Letter_Of_Intent,
							tooltip: toolTipLoi,
						},
					],
					userProps: { direction: 'column', columnToDivide: 1 },
				},
			]),
	];

	return struc;
};

const formStrucVPAnsprechpartner = (application: any) => {
	let struc: FormStrucItem[] = [];
	if (get(application, P.Allgemein_Antragsformular.path) === ANTRAGS_FORMULAREN.Loewe) {
		struc = [
			{
				fields: [
					{
						...P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin,
						type: FORM_FIELDS_TYPES.editableTable,
						hideColumns: [
							getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin.schema.attributes.Abteilung.path),
						],
						tableLabel: '',
						fieldProps: {
							Funktion: { width: '11rem' },
							Telefonnummer: { width: '9rem' },
						},
						preventLastElementDelete: true,
					},
				],
				userProps: { columnToDivide: 1 },
			},
		];
	}
	return struc;
};

// custom formstruc for specific table validation
const customFormStrucAnsprechpartner = (_application: any) => {
	return [
		{
			fields: [
				P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin.schema.attributes.Vorname,
				P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin.schema.attributes.Nachname,
				{
					...P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin.schema.attributes.Telefonnummer,
					validation: [{ validateFunction: Required }, { validateFunction: PhoneNumberValidation }],
				},
				{
					...P.Verbundpartner.schema.attributes.Verbundpartner_Ansprechpartnerin.schema.attributes.E_45_Mail_45_Adresse,
					validation: [{ validateFunction: Required }, { validateFunction: EmailValidation }],
				},
			],
			userProps: { columnToDivide: 2 },
		},
	];
};

export function getFormStrucVerbundpartnerSkizzeGlobal(application: any, _readOnly: boolean): FormStrucItem[] {
	const verbundpartner = get(application, P.Verbundpartner.path, [{}]);
	const institutionsOptionsArr = get(application, P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte.path);

	let completeStruc: FormStrucItem[] = [];
	verbundpartner.forEach((vbPartner: any, index: number) => {
		let verbundpartnerStruc: any[] = [];

		const antragsberechtigtAls = get(
			application,
			addIndexToPath(P.Verbundpartner.schema.attributes.Antragsberechtigt_Als.path, index, P.Verbundpartner.path),
			null,
		);
		const formStrucTypWithIndex = mapVerbundPartner(formStrucTyp(application, index), index);
		completeStruc = concat(completeStruc, formStrucTypWithIndex);

		// validate other fields only if "Antragsberechtigt als" is selected, otherwise only show "Antragsberechtigt als"-Field
		if (antragsberechtigtAls) {
			const isAntragsberechtigAlsHochschule = getIsAntragsberechtigAlsHochschule(application, index, institutionsOptionsArr);

			const isNeueHochschule = !!get(
				application,
				addIndexesToPath(P.Verbundpartner.schema.attributes.Neue_Hochschule.path, [`${index}`], ['Verbundpartner']),
				false,
			);

			const formStrucHochschuleWithIndex = mapVerbundPartner(formStrucSpalte1(isAntragsberechtigAlsHochschule, [], isNeueHochschule), index);
			const formStrucHochschuleExtraWithIndex = mapVerbundPartner(formStrucSpalte2(isAntragsberechtigAlsHochschule, isNeueHochschule), index);
			completeStruc = concat(completeStruc, formStrucHochschuleWithIndex, formStrucHochschuleExtraWithIndex);
		}

		const formStrucPruefAssistezWithIndex = mapVerbundPartner(formStrucPruefAssistenz(), index);
		const ansprechpartnerLength = get(vbPartner, 'Verbundpartner_Ansprechpartnerin', []).length;
		for (let apIndex = 0; apIndex < ansprechpartnerLength; apIndex++) {
			// verbundpartnerStruc = concat(verbundpartnerStruc, mapVerbundPartnerAnsprechpartner(customFormStrucAnsprechpartner(), index, apIndex));
			verbundpartnerStruc = concat(
				verbundpartnerStruc,
				mapVerbundPartnerAnsprechpartner(customFormStrucAnsprechpartner(application), index, apIndex),
			);
		}

		if (
			checkIfPruefAssitenzNeedsToValidate(
				vbPartner,
				ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisStatus,
				ANTRAG_VERBUNDPARTNER.UeberarbeitungHinweisErledigt,
			)
		) {
			completeStruc = concat(formStrucPruefAssistezWithIndex, completeStruc, verbundpartnerStruc);
		} else {
			completeStruc = concat(completeStruc, verbundpartnerStruc);
		}
	});

	return completeStruc;
}
